import { IconCalendar } from "@tabler/icons-react";
import {
  DateRangePicker as AriaDateRangePicker,
  Group,
  type DateRangePickerProps as AriaDateRangePickerProps,
  type DateValue,
  type ValidationResult,
} from "react-aria-components";

import { cn } from "@beweegbaas/ui/lib/utils";
import { Button } from "./button";
import { DateInput } from "./date-input";
import { FieldDescription, FieldError, FieldLabel } from "./field";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { RangeCalendar } from "./range-calendar";

export interface DateRangePickerProps<T extends DateValue> extends AriaDateRangePickerProps<T> {
  label?: string;
  description?: string;
  errorMessage?: string | ((validation: ValidationResult) => string);
  fieldGroupClassName?: string;
  className?: string;
}

export function DateRangePicker<T extends DateValue>({
  label,
  description,
  errorMessage,
  fieldGroupClassName,
  className,
  ...props
}: DateRangePickerProps<T>) {
  return (
    <AriaDateRangePicker {...props} className={cn("group flex flex-col gap-3", className)}>
      {label && <FieldLabel>{label}</FieldLabel>}
      <Group
        className={cn(
          "ring-input squircle not-squircle:rounded-md relative flex h-9 w-full min-w-0 items-center gap-0.5 bg-white shadow-xs ring-1 hover:ring-black/20 supports-squircle:rounded-full dark:bg-input/30 dark:hover:ring-white/20",
          "min-w-[220px]",
          fieldGroupClassName,
        )}
      >
        <DateInput
          slot="start"
          className={cn(
            // Override DateInput defaults (min-w-[150px], block) so two fields stay compact
            "inline-flex w-fit min-w-0 shrink-0 grow-0 px-2 py-1.5 text-sm",
            "placeholder:text-muted-foreground",
          )}
        />
        <span className="text-muted-foreground shrink-0 px-0.5 text-sm" aria-hidden>
          –
        </span>
        <DateInput
          slot="end"
          className={cn(
            "inline-flex w-fit min-w-0 shrink-0 grow-0 px-2 py-1.5 text-sm",
            "placeholder:text-muted-foreground",
          )}
        />
        {/* Eat remaining row width so start/end stay compact; calendar stays right like DatePicker */}
        <span className="min-h-0 min-w-0 flex-1 basis-0" aria-hidden />
        <Popover>
          <PopoverTrigger
            render={
              <Button
                size="icon"
                variant="secondary"
                className="mr-1.5 h-6 w-6 shrink-0 rounded-lg outline-offset-0"
                nativeButton={false}
              >
                <IconCalendar aria-hidden className="h-4 w-4" />
              </Button>
            }
          />
          <PopoverContent
            side="bottom"
            align="end"
            className={cn(
              // Do not use w-auto here: it overrides the default w-72 and collapses the panel
              // to the tiny trigger width. Size to calendar content with a sensible floor.
              "w-max max-w-[calc(100vw-2rem)] min-w-72 p-3",
            )}
          >
            <RangeCalendar firstDayOfWeek="mon" />
          </PopoverContent>
        </Popover>
      </Group>
      {description && <FieldDescription>{description}</FieldDescription>}
      {errorMessage && typeof errorMessage === "string" && <FieldError>{errorMessage}</FieldError>}
    </AriaDateRangePicker>
  );
}
